export type ToolSegment = 'operator' | 'utility' | 'entertainment';

export interface ToolCopyInput {
  slug: string;
  title: string;
  category: string;
}

export interface ToolCtaCopy {
  segment: ToolSegment;
  eyebrow: string;
  headline: string;
  body: string;
  primaryCta: string;
  claimHeadline: string;
  claimBody: string;
  claimCta: string;
  stickyLead: string;
  stickyBody: string;
  stickyCta: string;
  followUpHeadline: string;
  followUpBody: string;
}

const ENTERTAINMENT_SLUGS = new Set([
  'deep-sea-explorer',
  'org-market-wars',
  'orgle',
  'node-man',
  'org-run',
  'micro-machines',
]);

const UTILITY_SLUGS = new Set([
  'image-upscaler',
  'wifi-qr-poster',
  'storyboard-grid-printable',
]);

/**
 * Traffic from image utilities and arcade games is not the same audience as a
 * treasurer looking for an HOA budget. Keep the segments explicit so CTAs and
 * analytics can treat them differently.
 */
export function getToolSegment(category: string, slug: string): ToolSegment {
  const normalized = (category || '').toLowerCase();
  if (normalized === 'entertainment' || ENTERTAINMENT_SLUGS.has(slug)) {
    return 'entertainment';
  }
  if (normalized === 'utilities' || UTILITY_SLUGS.has(slug)) {
    return 'utility';
  }
  return 'operator';
}

function operatorCopy(tool: ToolCopyInput): Omit<ToolCtaCopy, 'segment'> {
  const slug = tool.slug.toLowerCase();
  const category = (tool.category || '').toLowerCase();

  if (slug.includes('hoa') || slug.includes('neighborhood') || slug.includes('community-land')) {
    return {
      eyebrow: 'For HOA boards & neighborhood associations',
      headline: 'Turn this into a board-ready workspace.',
      body: 'Keep the budget, reserve schedule, vendor bids, and board report in one place your whole board can access.',
      primaryCta: 'Create Free HOA Workspace',
      claimHeadline: 'Keep this work with your board',
      claimBody: 'Create a free workspace for your association. Your inputs stay on this device, and your dashboard will link you straight back to them.',
      claimCta: 'Create Free HOA Workspace',
      stickyLead: 'Managing an HOA or neighborhood association?',
      stickyBody: 'Give your board one place for budgets, reserves, vendors, and minutes.',
      stickyCta: 'Create Free HOA Workspace',
      followUpHeadline: 'Want a copy for the next board meeting?',
      followUpBody: 'Leave an email and we will send this HOA tool plus a short board-ready setup guide.',
    };
  }

  if (
    slug.includes('swim') ||
    slug.includes('sport') ||
    slug.includes('bracket') ||
    slug.includes('round-robin') ||
    slug.includes('tournament') ||
    slug.includes('team') ||
    slug.includes('draft') ||
    slug.includes('league') ||
    slug.includes('scout') ||
    slug.includes('camp')
  ) {
    return {
      eyebrow: 'For youth sports & team organizers',
      headline: 'Give parents one place to find this.',
      body: 'Schedules, dues, volunteer signups, and this result can live in a free team hub instead of a group text.',
      primaryCta: 'Create Free Team Hub',
      claimHeadline: 'Keep this with your team',
      claimBody: 'Create a free team hub for schedules, dues, and parent signups. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Team Hub',
      stickyLead: 'Running a team or league?',
      stickyBody: 'Replace the group text with one free hub for schedules, dues, and volunteers.',
      stickyCta: 'Create Free Team Hub',
      followUpHeadline: 'Want a copy for the team parents?',
      followUpBody: 'Leave an email and we will send this team tool plus a short setup guide for your season.',
    };
  }

  if (slug.includes('church') || slug.includes('worship') || slug.includes('ministry') || slug.includes('sermon')) {
    return {
      eyebrow: 'For churches & ministries',
      headline: 'Keep this with your ministry team.',
      body: 'Budgets, volunteers, campaigns, and service planning can live in one free church workspace.',
      primaryCta: 'Create Free Church Workspace',
      claimHeadline: 'Keep this with your church',
      claimBody: 'Create a free workspace for your ministry. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Church Workspace',
      stickyLead: 'Managing church operations?',
      stickyBody: 'Give your team one place for budgets, volunteers, and campaign planning.',
      stickyCta: 'Create Free Church Workspace',
      followUpHeadline: 'Want a copy for your leadership team?',
      followUpBody: 'Leave an email and we will send this church tool plus a short setup guide.',
    };
  }

  if (slug.includes('union') || slug.includes('guild') || slug.includes('apprentice') || slug.includes('wage')) {
    return {
      eyebrow: 'For unions, guilds & locals',
      headline: 'Keep this with your local.',
      body: 'Dues splits, apprentice ratios, wage progression, and chapter records can live in one free workspace.',
      primaryCta: 'Create Free Local Workspace',
      claimHeadline: 'Keep this with your local',
      claimBody: 'Create a free workspace for your local or guild. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Local Workspace',
      stickyLead: 'Managing a union or guild?',
      stickyBody: 'Keep dues, ratios, and chapter records in one shared workspace.',
      stickyCta: 'Create Free Local Workspace',
      followUpHeadline: 'Want a copy for your chapter?',
      followUpBody: 'Leave an email and we will send this union tool plus a short setup guide.',
    };
  }

  if (slug.includes('greek') || slug.includes('panhellenic') || slug.includes('alumni') || slug.includes('chapter-house')) {
    return {
      eyebrow: 'For chapters & alumni groups',
      headline: 'Keep this with your chapter.',
      body: 'Rush numbers, house budgets, alumni outreach, and dues can live in one free chapter workspace.',
      primaryCta: 'Create Free Chapter Hub',
      claimHeadline: 'Keep this with your chapter',
      claimBody: 'Create a free chapter workspace. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Chapter Hub',
      stickyLead: 'Running a chapter or alumni group?',
      stickyBody: 'Centralize rush, house, dues, and alumni records in one place.',
      stickyCta: 'Create Free Chapter Hub',
      followUpHeadline: 'Want a copy for your chapter?',
      followUpBody: 'Leave an email and we will send this chapter tool plus a short setup guide.',
    };
  }

  if (
    slug.includes('nonprofit') ||
    slug.includes('grant') ||
    slug.includes('fundrais') ||
    slug.includes('donation') ||
    slug.includes('philanthropy') ||
    slug.includes('endowment') ||
    slug.includes('booster') ||
    slug.includes('pta')
  ) {
    return {
      eyebrow: 'For nonprofits & fundraising teams',
      headline: 'Keep this result where your team can use it.',
      body: 'Budgets, volunteers, grants, and donor records can live in one free nonprofit workspace.',
      primaryCta: 'Create Free Nonprofit Workspace',
      claimHeadline: 'Keep this with your nonprofit',
      claimBody: 'Create a free workspace for your organization. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Nonprofit Workspace',
      stickyLead: 'Running a nonprofit or fundraiser?',
      stickyBody: 'Bring budgets, volunteers, and donor records into one free workspace.',
      stickyCta: 'Create Free Nonprofit Workspace',
      followUpHeadline: 'Want a copy for your board?',
      followUpBody: 'Leave an email and we will send this nonprofit tool plus a short setup guide.',
    };
  }

  if (category === 'governance') {
    return {
      eyebrow: 'For boards & governance teams',
      headline: 'Give this result a permanent home.',
      body: 'Agendas, minutes, motions, quorum checks, and bylaws belong with the rest of your board records.',
      primaryCta: 'Create Free Board Workspace',
      claimHeadline: 'Keep this with your board',
      claimBody: 'Create a free board workspace for agendas, minutes, and governance records. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Board Workspace',
      stickyLead: 'Like this governance tool?',
      stickyBody: 'Give your board one place for agendas, minutes, motions, and records.',
      stickyCta: 'Create Free Board Workspace',
      followUpHeadline: 'Want a copy for the meeting packet?',
      followUpBody: 'Leave an email and we will send this governance tool plus a short board setup guide.',
    };
  }

  if (category === 'finance') {
    return {
      eyebrow: 'For treasurers & finance teams',
      headline: 'Keep these numbers where the team can see them.',
      body: 'Replace the spreadsheet handoff with one free workspace for budgets, dues, and financial records.',
      primaryCta: 'Create Free Finance Workspace',
      claimHeadline: 'Keep this with your organization',
      claimBody: 'Create a free workspace for your numbers. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Finance Workspace',
      stickyLead: 'Tired of spreadsheet handoffs?',
      stickyBody: 'Keep budgets, dues, and financial records in one shared workspace.',
      stickyCta: 'Create Free Finance Workspace',
      followUpHeadline: 'Want a copy for your treasurer?',
      followUpBody: 'Leave an email and we will send this finance tool plus a short setup guide.',
    };
  }

  if (category === 'analytics') {
    return {
      eyebrow: 'For membership & operations teams',
      headline: 'Turn this estimate into a shared plan.',
      body: 'Keep the numbers, assumptions, and next steps together so your team can act on them.',
      primaryCta: 'Create Free Workspace',
      claimHeadline: 'Keep this with your team',
      claimBody: 'Create a free workspace for your organization. Your inputs stay on this device, and your dashboard will link you back to them.',
      claimCta: 'Create Free Workspace',
      stickyLead: 'Want to act on these numbers?',
      stickyBody: 'Keep the assumptions, targets, and next steps in one shared workspace.',
      stickyCta: 'Create Free Workspace',
      followUpHeadline: 'Want a copy for your team?',
      followUpBody: 'Leave an email and we will send this tool plus a short setup guide.',
    };
  }

  return {
    eyebrow: 'For community organizers',
    headline: 'Keep this result where your team can find it.',
    body: 'Save the draft on this device, copy a link with your inputs, or create a free workspace for your organization.',
    primaryCta: 'Create Free Organization',
    claimHeadline: 'Keep this with your organization',
    claimBody: 'Create a free workspace for your team. Your inputs stay on this device, and your dashboard will link you back to them.',
    claimCta: 'Create Free Organization',
    stickyLead: 'Like this tool?',
    stickyBody: 'OrgSets gives your team a permanent home for rosters, finances, and announcements.',
    stickyCta: 'Create Free Organization',
    followUpHeadline: 'Want a copy for your team?',
    followUpBody: 'Leave an email and we will send this tool plus a short setup guide.',
  };
}

export function getToolCtaCopy(tool: ToolCopyInput): ToolCtaCopy {  const segment = getToolSegment(tool.category, tool.slug);

  if (segment === 'entertainment') {
    return {
      segment,
      eyebrow: 'Free game',
      headline: 'Take a break. Your organization can wait.',
      body: 'This one is just for fun. When you are ready to get back to running things, there are 100+ free calculators and templates in the toolbox.',
      primaryCta: 'Browse Free Tools',
      claimHeadline: 'Browse the free toolbox',
      claimBody: 'Explore free calculators and templates for boards, teams, and nonprofits.',
      claimCta: 'Browse Free Tools',
      stickyLead: 'Having fun?',
      stickyBody: 'Explore 100+ free tools for boards, teams, and nonprofits.',
      stickyCta: 'Browse Free Tools',
      followUpHeadline: 'Want more free tools?',
      followUpBody: 'Leave an email and we will send the occasional OrgSets toolbox update.',
    };
  }

  if (segment === 'utility') {
    return {
      segment,
      eyebrow: 'Free utility',
      headline: 'Done with this file?',
      body: 'Save a draft, copy a link with your settings, or explore 100+ free tools built for organizations.',
      primaryCta: 'Browse Free Tools',
      claimHeadline: 'Explore the free toolbox',
      claimBody: 'Find free calculators and templates for boards, teams, and nonprofits.',
      claimCta: 'Browse Free Tools',
      stickyLead: 'Need another free tool?',
      stickyBody: 'Explore 100+ calculators and templates for organizations.',
      stickyCta: 'Browse Free Tools',
      followUpHeadline: 'Want more free tools?',
      followUpBody: 'Leave an email and we will send the occasional OrgSets toolbox update.',
    };
  }

  return {
    segment,
    ...operatorCopy(tool),
  };
}

/**
 * Form placeholders should look like the visitor's world, not a swim club's.
 * Kept separate from CTA copy so the modal stays a dumb presenter.
 */
export function getToolFormPlaceholders(tool: ToolCopyInput): {
  org: string;
  name: string;
  email: string;
} {
  const slug = tool.slug.toLowerCase();
  const category = (tool.category || '').toLowerCase();

  if (slug.includes('hoa') || slug.includes('neighborhood')) {
    return {
      org: 'e.g. Pinecrest Homeowners Association',
      name: 'e.g. Dana Whitfield',
      email: 'you@yourassociation.org',
    };
  }
  if (slug.includes('sport') || slug.includes('swim') || slug.includes('league') || slug.includes('team')) {
    return {
      org: 'e.g. Austin Blue Fins Swim Club',
      name: 'e.g. Coach Sarah Miller',
      email: 'sarah@aquatics.org',
    };
  }
  if (slug.includes('church') || slug.includes('ministry') || slug.includes('worship')) {
    return {
      org: 'e.g. St. Mark Lutheran Church',
      name: 'e.g. Pastor Dana Reed',
      email: 'office@stmark.org',
    };
  }
  if (slug.includes('union') || slug.includes('guild') || slug.includes('local')) {
    return {
      org: 'e.g. Local 402 — Stagehands',
      name: 'e.g. Alex Rivera',
      email: 'alex@local402.org',
    };
  }
  if (slug.includes('greek') || slug.includes('chapter') || slug.includes('alumni')) {
    return {
      org: 'e.g. Delta Tau Chapter',
      name: 'e.g. Jordan Lee',
      email: 'president@deltatau.org',
    };
  }
  if (slug.includes('nonprofit') || slug.includes('grant') || slug.includes('fundrais') || category === 'finance') {
    return {
      org: 'e.g. Riverside Community Fund',
      name: 'e.g. Morgan Chen',
      email: 'morgan@riversidefund.org',
    };
  }
  return {
    org: 'e.g. Riverside Community Association',
    name: 'e.g. Morgan Chen',
    email: 'you@yourorganization.org',
  };
}
